// "Preview" (issue 40): how the post will look on a phone, drawn by the one renderer. Two generic
// looks (no platform names or logos in the frame), the carousel swipeable, the caption and hashtags
// of the matching channel under or above the picture, and for 9:16 the zones an app interface
// usually covers, shaded. Read-only: it never changes the layout, so it works on approved items.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../components/ui';
import type { Layout } from '../../lib/contentTypes';
import { renderArtboard, type RenderEnv } from './render';

type Look = 'feed' | 'professional';
/** Which caption each look shows: the first channel of the list that has one. */
const LOOK_CHANNELS: Record<Look, string[]> = { feed: ['instagram', 'facebook', 'tiktok'], professional: ['linkedin', 'x'] };
const LOOK_LABEL: Record<Look, string> = { feed: 'Feed (square/portrait)', professional: 'Professional feed' };

export function PostPreview({ layout, env, title, onClose }: { layout: Layout; env: RenderEnv; title: string; onClose: () => void }) {
  const [look, setLook] = useState<Look>('feed');
  const [index, setIndex] = useState(0);
  const [zones, setZones] = useState(true);
  const n = layout.artboards.length;
  const vertical = layout.height / layout.width > 1.5;
  const frameW = Math.max(220, Math.min(340, window.innerWidth - 90));
  const frameH = Math.round((frameW * layout.height) / layout.width);

  // --- draw every slide once, at the frame's pixel size ----------------------------------------
  const canvases = useRef<(HTMLCanvasElement | null)[]>([]);
  useEffect(() => {
    const dpr = window.devicePixelRatio || 1;
    layout.artboards.forEach((ab, i) => {
      const c = canvases.current[i];
      if (!c) return;
      c.width = Math.round(frameW * dpr);
      c.height = Math.round(frameH * dpr);
      const ctx = c.getContext('2d')!;
      const s = (frameW * dpr) / layout.width;
      ctx.setTransform(s, 0, 0, s, 0, 0);
      renderArtboard(ctx, layout, ab, i, env);
    });
    // a look puts the picture elsewhere in the tree: its canvases are new and blank, so redraw
  }, [layout, env, frameW, frameH, look]);

  // --- swipe: drag the strip, arrows, dots, keys ----------------------------------------------
  const [dx, setDx] = useState(0);
  const drag = useRef<{ x0: number; moved: boolean } | null>(null);
  const go = (to: number) => setIndex(Math.min(n - 1, Math.max(0, to)));
  const keys = useRef({ index, n });
  keys.current = { index, n };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.closest('input, textarea, select')) return;
      if (e.key === 'ArrowRight') setIndex(Math.min(keys.current.n - 1, keys.current.index + 1));
      if (e.key === 'ArrowLeft') setIndex(Math.max(0, keys.current.index - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  function down(ev: React.PointerEvent) {
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    drag.current = { x0: ev.clientX, moved: false };
  }
  function move(ev: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const delta = ev.clientX - d.x0;
    if (Math.abs(delta) > 3) d.moved = true;
    // resist at both ends
    const atEdge = (index === 0 && delta > 0) || (index === n - 1 && delta < 0);
    setDx(atEdge ? delta / 4 : delta);
  }
  function up(ev: React.PointerEvent) {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const delta = ev.clientX - d.x0;
    setDx(0);
    if (Math.abs(delta) > frameW * 0.2) go(index + (delta < 0 ? 1 : -1));
  }

  // --- caption of the matching channel ----------------------------------------------------------
  const channel = LOOK_CHANNELS[look].find((c) => layout.meta.captions[c]);
  const caption = channel ? layout.meta.captions[channel] : null;
  const text = caption?.text.trim() ?? '';
  const tags = caption?.hashtags.join(' ') ?? '';
  const account = 'Your account';

  const media = (
    <div className="cs-pv-media" style={{ width: frameW, height: frameH }}>
      <div
        className="cs-pv-track"
        data-testid="preview-track"
        data-index={index}
        style={{ width: frameW * n, transform: `translateX(${-index * frameW + dx}px)`, transition: drag.current ? 'none' : 'transform 220ms ease' }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      >
        {layout.artboards.map((ab, i) => (
          <canvas key={ab.id} ref={(el) => {
              canvases.current[i] = el;
            }} style={{ width: frameW, height: frameH }} aria-label={`Slide ${i + 1} of ${n}`} role="img" />
        ))}
      </div>
      {vertical && zones && (
        <>
          <div className="cs-pv-zone top" aria-hidden="true">
            <span>top bar</span>
          </div>
          <div className="cs-pv-zone bottom" aria-hidden="true">
            <span>caption and buttons</span>
          </div>
          <div className="cs-pv-zone side" aria-hidden="true">
            <span>buttons</span>
          </div>
        </>
      )}
      {n > 1 && (
        <>
          <button type="button" className="cs-pv-arrow prev" onClick={() => go(index - 1)} disabled={index === 0} aria-label="Previous slide">
            ‹
          </button>
          <button type="button" className="cs-pv-arrow next" onClick={() => go(index + 1)} disabled={index === n - 1} aria-label="Next slide">
            ›
          </button>
        </>
      )}
    </div>
  );
  const dots = useMemo(
    () =>
      n > 1 ? (
        <div className="cs-pv-dots" role="group" aria-label="Slides">
          {layout.artboards.map((ab, i) => (
            <button key={ab.id} type="button" className={`cs-pv-dot${i === index ? ' on' : ''}`} onClick={() => go(i)} aria-label={`Go to slide ${i + 1}`} aria-current={i === index} />
          ))}
        </div>
      ) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [n, index, layout.artboards],
  );
  const header = (
    <div className="cs-pv-head">
      <span className="cs-pv-avatar" aria-hidden="true" />
      <span className="cs-pv-name">{account}</span>
    </div>
  );
  const captionBlock = (
    <div className="cs-pv-caption" data-testid="preview-caption">
      {text || tags ? (
        <>
          {text && <p>{text}</p>}
          {tags && <p className="cs-pv-tags">{tags}</p>}
        </>
      ) : (
        <p className="cs-pv-none">No caption written for this look yet.</p>
      )}
    </div>
  );

  return (
    <Modal title={`Preview · ${title}`} onClose={onClose} wide>
      <div className="cs-pv" data-testid="post-preview" data-look={look}>
        <div className="row gap-s wrap" role="group" aria-label="Look">
          {(Object.keys(LOOK_LABEL) as Look[]).map((k) => (
            <button key={k} type="button" className={`btn small${look === k ? ' primary' : ''}`} aria-pressed={look === k} onClick={() => setLook(k)}>
              {LOOK_LABEL[k]}
            </button>
          ))}
          {vertical && (
            <label className="check small">
              <input type="checkbox" checked={zones} onChange={(ev) => setZones(ev.target.checked)} /> show what the app interface covers
            </label>
          )}
        </div>
        <div className={`cs-pv-phone ${look}${vertical ? ' vertical' : ''}`} style={{ width: frameW + 24 }}>
          {vertical ? (
            <>
              {media}
              {dots}
              {captionBlock}
            </>
          ) : look === 'feed' ? (
            <>
              {header}
              {media}
              <div className="cs-pv-actions" aria-hidden="true">
                <span>♡</span>
                <span>◯</span>
                <span>➤</span>
              </div>
              {dots}
              {captionBlock}
            </>
          ) : (
            <>
              {header}
              {captionBlock}
              {media}
              {dots}
              <div className="cs-pv-actions pro" aria-hidden="true">
                <span>Like</span>
                <span>Comment</span>
                <span>Share</span>
              </div>
            </>
          )}
        </div>
        <p className="small muted">
          {channel ? `Caption of the “${channel}” channel.` : 'Add a caption in “Captions and alt text” to see it here.'}{' '}
          {n > 1 ? 'Drag the picture, or use the arrows, to swipe. ' : ''}A close approximation: each app changes its look from time to time.
        </p>
      </div>
    </Modal>
  );
}
